import { useTranslation } from '@rhino-project/core/i18n';
import { useMemo } from 'react';
import {
  useModelIndexContext,
  useGlobalComponentForAttribute,
  useModelIndex,
  useTableInheritedProps,
  useModelAndAttributeFromPath,
  TableInheritedProps
} from '@rhino-project/core/hooks';
import { getModelFromRef } from '@rhino-project/core/utils';
import {
  RhinoAttribute,
  RhinoResource,
  RhinoResourceSpecifier
} from '@rhino-project/core';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectIndicator,
  SelectPopover,
  ListBox,
  ListBoxItem
} from '@heroui/react';
export const ModelEditableCellReferenceBase = ({
  model,
  ...props
}: TableInheritedProps) => {
  const { t } = useTranslation();
  const {
    path,
    row,
    getValue,
    filter,
    limit = 100,
    offset,
    order,
    search
  } = props;
  const { attribute } = useModelAndAttributeFromPath(
    model as RhinoResourceSpecifier,
    path as string
  );
  const refModel = useMemo(
    () => getModelFromRef(attribute as RhinoAttribute),
    [attribute]
  );

  const { results } = useModelIndex(refModel as RhinoResource, {
    search: search as string | undefined,
    filter: filter as Record<string, unknown> | undefined,
    limit: limit as number,
    offset: offset as number | undefined,
    order: order as string[] | undefined
  });

  const {
    update: { mutate }
  } = useModelIndexContext();
  const { inheritedProps } = useTableInheritedProps(props);

  const options = useMemo(
    () =>
      // Rhino records carry id/display_name at runtime, but the generated
      // resource record types don't surface display_name here.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      results?.map((result: any) => (
        <ListBoxItem key={`${result.id}`} id={`${result.id}`}>
          {result.display_name}
        </ListBoxItem>
      )),
    [results]
  );

  return (
    <Select
      aria-label={attribute?.readableName || (path as string)}
      placeholder={t('rhino.frontend.messages.select_a_value', {
        value1: (attribute?.readableName || (path as string)).toLowerCase()
      })}
      // disallowEmptySelection is not part of HeroUI's Select prop types but is
      // preserved here to keep runtime behavior identical to the original.
      {...({ disallowEmptySelection: true } as object)}
      value={`${getValue!()?.id || getValue!() || ''}`}
      onChange={(key) => {
        mutate({
          id: (row as { original: { id: unknown } }).original.id,
          [path as string]: key
        });
      }}
      {...inheritedProps}
    >
      <SelectTrigger>
        <SelectValue />
        <SelectIndicator />
      </SelectTrigger>
      <SelectPopover>
        <ListBox>{options}</ListBox>
      </SelectPopover>
    </Select>
  );
};

export const ModelEditableCellReference = (props: TableInheritedProps) =>
  useGlobalComponentForAttribute(
    'ModelEditableCellReference',
    ModelEditableCellReferenceBase,
    props
  );
